import {
  emptyCell,
  formatFxList,
  formatNoteName,
  formatVolume,
  isNoteOffToken,
  parseFxList,
  parseNoteName,
  parseVolumeToken,
  type PatternCell,
} from "../../../../src/lib/audio";

export const NOTE_WIDTH = 4;
export const VOL_WIDTH = 2;
/** Fits 4 glued effects with 2-digit values (e.g. `C04?50R04F80`). */
export const FX_WIDTH = 12;
export const CELL_WIDTH = NOTE_WIDTH + VOL_WIDTH + FX_WIDTH;

/** Fixed body row height — must match `.tracker tbody tr.tracker-row` in CSS. */
export const TRACKER_ROW_HEIGHT_PX = 18;
export const ROW_OVERSCAN = 12;

/** Track column width in `ch` (content); CSS adds padding via calc — see tracker.css. */
export const TRACKER_TRACK_WIDTH_CH = 18;
export const COL_OVERSCAN = 4;

export type TrackerCursor = {
  step: number;
  track: number;
  /** 0 .. CELL_WIDTH-1 within the track cell */
  offset: number;
};

export function padField(value: string, width: number, fill = "."): string {
  return value.slice(0, width).padEnd(width, fill);
}

/** Pack a cell into fixed NOTE(4)+VOL(2)+FX overwrite buffer. */
export function encodeCell(cell: PatternCell | undefined): string {
  if (
    !cell
    || (cell.note === undefined && !cell.noteOff && cell.volume === undefined && !cell.fx?.length)
  ) {
    return ".".repeat(CELL_WIDTH);
  }

  let note = "....";
  if (cell.noteOff && cell.note === undefined) {
    note = padField("---", NOTE_WIDTH, " ");
  } else if (cell.note !== undefined) {
    note = padField(formatNoteName(cell.note), NOTE_WIDTH, " ");
  }

  const vol = cell.volume === undefined ? ".." : padField(formatVolume(cell.volume), VOL_WIDTH, "0");
  const fx = cell.fx?.length
    ? padField(formatFxList(cell.fx), FX_WIDTH, ".")
    : ".".repeat(FX_WIDTH);
  return `${note}${vol}${fx}`;
}

export function decodeCell(raw: string): PatternCell {
  const packed = padField(raw, CELL_WIDTH);
  // Note field often still has trailing `.` from the empty template (`C-3.`).
  const noteTok = packed.slice(0, NOTE_WIDTH).replace(/\.+$/g, "").trimEnd();
  const volTok = packed.slice(NOTE_WIDTH, NOTE_WIDTH + VOL_WIDTH);
  const fxTok = packed.slice(NOTE_WIDTH + VOL_WIDTH);

  const cell = emptyCell();

  if (isNoteOffToken(noteTok) || isNoteOffToken(noteTok.replace(/\s+/g, ""))) {
    cell.noteOff = true;
  } else if (noteTok && !/^[\.\s]*$/.test(noteTok)) {
    const note = parseNoteName(noteTok.trim()) ?? parseNoteName(noteTok.replace(/\s+/g, ""));
    if (note !== undefined) {
      cell.note = note;
    }
  }

  if (volTok && !/^\.+$/.test(volTok)) {
    const volume = parseVolumeToken(volTok);
    if (volume !== undefined) {
      cell.volume = volume;
    }
  }

  if (fxTok && !/^\.+$/.test(fxTok)) {
    const fx = parseFxList(fxTok);
    if (fx.length > 0) {
      cell.fx = fx;
    }
  }

  return cell;
}

export function fieldForOffset(offset: number): "note" | "vol" | "fx" {
  if (offset < NOTE_WIDTH) {
    return "note";
  }
  if (offset < NOTE_WIDTH + VOL_WIDTH) {
    return "vol";
  }
  return "fx";
}

/** Default octave when auto-filling after a note letter. */
export const DEFAULT_NOTE_OCTAVE = 4;

/** Note-name column (A–G); accidentals and octave live in later offsets. */
export function isNoteLetter(char: string): boolean {
  return /^[A-Ga-g]$/.test(char);
}

/**
 * True when the note field already has an octave digit (offsets 2…).
 * Used so retyping a letter on `C-4` / `C#3` keeps the existing octave.
 */
export function noteFieldHasOctave(buffer: string): boolean {
  const note = padField(buffer.slice(0, NOTE_WIDTH), NOTE_WIDTH);
  for (let i = 2; i < NOTE_WIDTH; i++) {
    if (/\d/.test(note[i]!)) {
      return true;
    }
  }
  return false;
}

/** Single-digit tracker octave for auto-fill / remembered typing. */
export function clampNoteOctave(octave: number): number {
  if (!Number.isFinite(octave)) {
    return DEFAULT_NOTE_OCTAVE;
  }
  return Math.min(9, Math.max(0, Math.trunc(octave)));
}

/** Patch a glyph span without thrashing when the char is unchanged. */
export function setGlyph(el: HTMLElement, char: string): void {
  if (el.textContent !== char) {
    el.textContent = char;
  }
  const isDot = char === ".";
  if (el.classList.contains("dot") !== isDot) {
    el.classList.toggle("dot", isDot);
  }
}

export function computeVisibleRange(opts: {
  steps: number;
  scrollTop: number;
  clientHeight: number;
  rowHeightPx?: number;
  overscan?: number;
}): { start: number; end: number } {
  const rowHeight = opts.rowHeightPx ?? TRACKER_ROW_HEIGHT_PX;
  const overscan = opts.overscan ?? ROW_OVERSCAN;
  const steps = opts.steps;
  if (steps <= 0) {
    return { start: 0, end: 0 };
  }
  const viewRows = Math.max(1, Math.ceil(opts.clientHeight / rowHeight));
  const start = Math.max(0, Math.floor(opts.scrollTop / rowHeight) - overscan);
  const end = Math.min(steps, start + viewRows + overscan * 2);
  return { start, end };
}

/**
 * Horizontal track window for column virtualization.
 * `scrollLeft` is relative to the full table (step column + tracks).
 * Window size is stable across the scroll range so pad + column counts
 * do not change total scrollWidth mid-drag.
 */
export function computeVisibleTrackRange(opts: {
  tracks: number;
  scrollLeft: number;
  clientWidth: number;
  trackWidthPx: number;
  stepWidthPx?: number;
  overscan?: number;
}): { start: number; end: number } {
  const tracks = opts.tracks;
  if (tracks <= 0) {
    return { start: 0, end: 0 };
  }
  const trackW = Math.max(1, opts.trackWidthPx);
  const stepW = Math.max(0, opts.stepWidthPx ?? 0);
  const overscan = opts.overscan ?? COL_OVERSCAN;
  // Visible track strip is the viewport minus the sticky step gutter.
  const trackViewport = Math.max(trackW, opts.clientWidth - stepW);
  const viewTracks = Math.max(1, Math.ceil(trackViewport / trackW));
  const windowSize = Math.min(tracks, viewTracks + overscan * 2);
  // Track i occupies [stepW + i*trackW, stepW + (i+1)*trackW) in table coords.
  let start = Math.floor((opts.scrollLeft - stepW) / trackW) - overscan;
  start = Math.max(0, start);
  let end = start + windowSize;
  if (end > tracks) {
    end = tracks;
    start = Math.max(0, end - windowSize);
  }
  return { start, end };
}

export function flatOf(track: number, offset: number): number {
  return track * CELL_WIDTH + offset;
}

export function cursorFromFlat(flat: number): { track: number; offset: number } {
  const track = Math.floor(flat / CELL_WIDTH);
  const offset = flat - track * CELL_WIDTH;
  return { track, offset };
}
